import { leadFullName, type LeadRecord, type PaymentMode } from '@/lib/sales-metrics';

// The full onboarding form captured in Step 1 of the wizard. All values are
// strings (persisted as a JSON snapshot on the lead via the onboard endpoint).
export interface LeadDetailsForm {
  arnHolderName: string;
  arnAum: string;
  // ARN holder's own address (from the ARN/MFAPI lookup) - kept separate from
  // `address` below, which is the business's GST-registered address.
  arnAddress: string;
  primaryNumber: string;
  secondaryNumber: string;
  primaryEmail: string;
  secondaryEmail: string;
  websiteUrl: string;
  gstin: string;
  legalName: string;
  displayName: string;
  pincode: string;
  city: string;
  state: string;
  address: string;
  lang1: string;
  lang2: string;
  lang3: string;
  remarks: string;
  createdDate: string;
  modifiedDate: string;
}

export const EMPTY_FORM: LeadDetailsForm = {
  arnHolderName: '',
  arnAum: '',
  arnAddress: '',
  primaryNumber: '',
  secondaryNumber: '',
  primaryEmail: '',
  secondaryEmail: '',
  websiteUrl: '',
  gstin: '',
  legalName: '',
  displayName: '',
  pincode: '',
  city: '',
  state: '',
  address: '',
  lang1: '',
  lang2: '',
  lang3: '',
  remarks: '',
  createdDate: '',
  modifiedDate: '',
};

// Modes that record money actually received. Every dropdown that means "how did
// the client pay?" uses this list.
export const SETTLED_PAYMENT_MODES: PaymentMode[] = ['UPI', 'Bank Transfer', 'Razorpay'];

// The Billing Handover list, which additionally offers 'Due' - service starts
// now, the client pays by the date entered beside it (ADR-053).
export const PAYMENT_MODES: PaymentMode[] = [...SETTLED_PAYMENT_MODES, 'Due'];

/**
 * The modes offered for one installment.
 *
 * 'Due' only appears on the FIRST. It is the decision to start a client's
 * service before they have paid, and service starts exactly once - with
 * installment #1. By #2 the client is already being served, so there is no such
 * decision left to make: a later installment is simply collected, and one that
 * goes unpaid is overdue rather than "due". The server enforces this too.
 */
export const paymentModesFor = (installmentNumber: number): PaymentMode[] =>
  installmentNumber === 1 ? PAYMENT_MODES : SETTLED_PAYMENT_MODES;

// Seed the Step 1 form from the lead (and any previously saved form snapshot).
export const buildInitialForm = (lead: LeadRecord): LeadDetailsForm => ({
  ...EMPTY_FORM,
  ...(lead.form ?? {}),
  arnHolderName: lead.form?.arnHolderName || leadFullName(lead),
  primaryNumber: lead.form?.primaryNumber || lead.mobile,
  primaryEmail: lead.form?.primaryEmail || lead.email,
  pincode: lead.form?.pincode || lead.pincode || '',
  city: lead.form?.city || lead.city || '',
  state: lead.form?.state || lead.state || '',
  createdDate: lead.form?.createdDate || new Date().toISOString(),
});
